
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';

export interface DataExportRequest {
  id: string;
  user_id: string;
  status: string;
  export_format: string;
  file_url: string | null;
  file_size: number | null;
  created_at: string;
  completed_at: string | null;
  expires_at: string | null;
}

/**
 * Request a complete data export for the current user
 */
export const requestDataExport = async (
  format: 'json' | 'csv' | 'both' = 'json'
): Promise<{ success: boolean; requestId?: string; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    console.log('📤 Requesting data export...');

    // Check for existing pending/processing requests
    const { data: existing } = await supabase
      .from('data_export_requests')
      .select('*')
      .eq('user_id', user.id)
      .in('status', ['pending', 'processing'])
      .single();

    if (existing) {
      toast.info('You already have a pending export request');
      return { success: false, error: 'Existing request pending' };
    }

    // Check rate limit (1 per 24 hours)
    const { data: recent } = await supabase
      .from('data_export_requests')
      .select('created_at')
      .eq('user_id', user.id)
      .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
      .limit(1);

    if (recent && recent.length > 0) {
      toast.error('You can only request one export per 24 hours');
      return { success: false, error: 'Rate limit exceeded' };
    }

    // Create export request
    const { data: request, error } = await supabase
      .from('data_export_requests')
      .insert({
        user_id: user.id,
        export_format: format,
        status: 'pending'
      })
      .select()
      .single();

    if (error) throw error;

    // Trigger export edge function
    const { data: exportData, error: exportError } = await supabase.functions.invoke(
      'export-user-data',
      {
        body: { requestId: request.id, format }
      }
    );

    if (exportError) {
      console.error('Export function error:', exportError);
      // Update request status to failed
      await supabase
        .from('data_export_requests')
        .update({ status: 'failed' })
        .eq('id', request.id);
      
      throw exportError;
    }

    toast.success('Data export started! You will be notified when it\'s ready.');
    return { success: true, requestId: request.id };

  } catch (error: any) {
    console.error('Error requesting data export:', error);
    toast.error('Failed to request data export');
    return { success: false, error: error.message };
  }
};

/**
 * Get all export requests for the current user
 */
export const getExportRequests = async (): Promise<DataExportRequest[]> => {
  try {
    const { data, error } = await supabase
      .from('data_export_requests')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching export requests:', error);
    return [];
  }
};

/**
 * Download an exported data file
 */
export const downloadExport = async (fileUrl: string, fileName: string) => {
  try {
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success('Download started');
  } catch (error) {
    console.error('Error downloading export:', error);
    toast.error('Failed to download export');
  }
};
